 
/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { CopyOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { BASE_URL } from '../api';
import OrdersTable from './OrdersTable';
import { openOrderSuccess } from './OrderSuccessModal';
import { cleanBybitFromMessage } from '../utils/messageUtils';


const TakeProfitPopup = ({ visible, order, currentPrice, onClose, fetchOrders }) => {
  const [tpForm, setTpForm] = useState({ qty: '', price: '' });
  const [tpOrders, setTpOrders] = useState([]);
  const [tpLoading, setTpLoading] = useState(false);
  const [liveCurrentPrice, setLiveCurrentPrice] = useState(currentPrice || '');
  const [ordersTableRefreshKey, setOrdersTableRefreshKey] = useState(0);
  const [showCloseOption, setShowCloseOption] = useState(false);
  const [qtyValidationMessage, setQtyValidationMessage] = useState('');

  useEffect(() => {
    if (visible && order && order._id) {
      fetchTpOrders(order._id).then(() => {
        const { onHold, sold } = getTpQuantities();
        const filledQty = order ? (parseFloat(order.filledQuantity) || parseFloat(order.count) || 0) : 0;
        const maxQty = Math.max(0, filledQty - onHold - sold);
        setTpForm(prev => ({ ...prev, qty: maxQty.toString() }));
      });
    }
  }, [visible, order]);

  useEffect(() => {
    let interval;
    async function fetchPrice() {
      if (order && order.token) {
        try {
          const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=linear&symbol=${order.token}`);
          const data = await res.json();
          setLiveCurrentPrice(data?.result?.list?.[0]?.markPrice || '');
        } catch {
          setLiveCurrentPrice('');
        }
      }
    }
    if (visible && order && order.token) {
      fetchPrice();
      interval = setInterval(fetchPrice, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [visible, order]);

  const fetchTpOrders = async (LinkedOrderId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/orders?orderNature=tp&orderID=${LinkedOrderId}`, { credentials: 'include' });
      const data = await res.json();
      setTpOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setTpOrders([]);
    }
  };

   // Reset form fields when popup is opened
  useEffect(() => {
    if (visible) {
      setTpForm({ qty: '', price: '' });
      setQtyValidationMessage('');
    }
  }, [visible]);

  // Calculate Quantity on hold and Sold quantity
  const getTpQuantities = () => {
    let onHold = 0;
    let sold = 0;
    let hasUntriggeredOrders = false;
    tpOrders.forEach(tp => {
      const status = (tp.orderStatus || '').toLowerCase();
      const qty = parseFloat(tp.count) || 0;
      if (status === 'untriggered' || status === 'new' || status === 'placed') {
        onHold += qty;
        hasUntriggeredOrders = true;
      }
      if (status === 'activated' || status === 'filled' || status === 'triggered') {
        sold += qty;
      }
    });
    return { onHold, sold, hasUntriggeredOrders };
  };

  const { onHold, sold } = getTpQuantities();
  const filledQty = order ? (parseFloat(order.filledQuantity) || parseFloat(order.count) || 0) : 0;
  const maxQty = Math.max(0, filledQty - onHold - sold);
    // const maxQty = filledQty +10;


  // --- normalizeQuantity.js logic (inline for browser use) ---
  function countDecimals(str) {
    const s = String(str);
    const dot = s.indexOf(".");
    return dot === -1 ? 0 : (s.length - dot - 1);
  }
  function toScaledBigInt(value, scale) {
    const s = String(value);
    const decs = countDecimals(s);
    if (decs === scale) return BigInt(s.replace(".", ""));
    if (decs < scale) {
      const zeros = "0".repeat(scale - decs);
      return BigInt((s.includes(".") ? s.replace(".", "") : s) + zeros);
    }
    const [intPart, fracPart = ""] = s.split(".");
    const keep = fracPart.slice(0, scale);
    const drop = fracPart.slice(scale);
    let core = BigInt((intPart || "0") + (keep || "").padEnd(scale, "0"));
    if (drop && /[1-9]/.test(drop)) {
      core += 1n;
    }
    return core;
  }
  function ceilToStep(n, step) {
    return ((n + (step - 1n)) / step) * step;
  }
  async function normalizeQuantity(symbol, desiredQuantity) {
    if (!symbol || desiredQuantity == null) {
      throw new Error("symbol and desiredQuantity are required.");
    }
    const url = new URL("https://api.bybit.com/v5/market/instruments-info");
    url.searchParams.set("category", "linear");
    url.searchParams.set("symbol", symbol);
    let data;
    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      data = await res.json();
    } catch (err) {
      throw new Error(`Failed to fetch Bybit instruments info: ${err.message}`);
    }
    if (data?.retCode !== 0 || !data?.result?.list?.length) {
      throw new Error(`Unexpected API response: retCode=${data?.retCode}`);
    }
    const lot = data.result.list[0]?.lotSizeFilter;
    if (!lot) throw new Error("lotSizeFilter not found for symbol.");
    const minOrderQtyStr = lot.minOrderQty;
    const qtyStepStr     = lot.qtyStep;
    if (minOrderQtyStr == null || qtyStepStr == null) {
      throw new Error("minOrderQty or qtyStep missing in lotSizeFilter.");
    }
    const decMin  = countDecimals(minOrderQtyStr);
    const decStep = countDecimals(qtyStepStr);
    const scale   = Math.max(decMin, decStep);
    const stepScaled = toScaledBigInt(qtyStepStr, scale);
    if (stepScaled <= 0n) throw new Error("Invalid qtyStep <= 0.");
    const minScaled = toScaledBigInt(minOrderQtyStr, scale);
    let desiredScaled = toScaledBigInt(desiredQuantity, scale);
    let adjustedScaled = ceilToStep(desiredScaled, stepScaled);
    const minAligned = ceilToStep(minScaled, stepScaled);
    if (adjustedScaled < minAligned) adjustedScaled = minAligned;
    const adjustedStr = adjustedScaled.toString();
    let result;
    if (scale === 0) {
      result = Number(adjustedStr);
    } else {
      const intPart = adjustedStr.slice(0, -scale) || "0";
      const fracPart = adjustedStr.slice(-scale).padStart(scale, "0");
      result = Number(`${intPart}.${fracPart}`);
    }
    return result;
  }

  const handleTpInput = async (e) => {
    const { name, value } = e.target;
    let val = value;
    if (name === 'qty') {
      // Clear any previous validation message
      setQtyValidationMessage('');
      if (val && (parseFloat(val) > maxQty)) {
        toast.error("You don't have that quantity", { position: 'top-right', autoClose: 5000 });
        val = maxQty;
      }
    } else if (name === 'price') {
      // Check if price is within ±2% of current price
      const checkTakeProfitPrice = (tpPrice) => {
        if (!tpPrice || !liveCurrentPrice) {
          setShowCloseOption(false);
          return;
        }
        const tp = parseFloat(tpPrice);
        const current = parseFloat(liveCurrentPrice);
        if (isNaN(tp) || isNaN(current) || current === 0) {
          setShowCloseOption(false);
          return;
        }
        const pctDiff = Math.abs(tp - current) / current * 100;
        setShowCloseOption(pctDiff <= 2);
      };

      checkTakeProfitPrice(val);
    }
    setTpForm(prev => ({ ...prev, [name]: val }));
  };

  // Small inline SVG spinner for inner-button loading
  const SpinnerSmall = ({ size = 16, color = '#fff' }) => (
    <svg width={size} height={size} viewBox="0 0 50 50" style={{ display: 'inline-block' }}>
      <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray="31.415,31.415" transform="rotate(-90 25 25)">
        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite" />
      </circle>
    </svg>
  );

  const handleTpSubmit = async () => {
    if (!tpForm.qty || !tpForm.price) return;
    if (parseFloat(tpForm.qty) === 0) {
      toast.error("Zero quantity not available to process", { position: 'top-right', autoClose: 5000 });
      return;
    }
    const { hasUntriggeredOrders } = getTpQuantities();
    if (hasUntriggeredOrders) {
      toast.error("Cannot place new order while there are untriggered/active orders", { position: 'top-right', autoClose: 5000 });
      return;
    }
    if (parseFloat(tpForm.qty) > maxQty) {
      toast.error("You don't have that quantity", { position: 'top-right', autoClose: 5000 });
      return;
    }
    
    // Normalize quantity on submit
    let normalizedQty = tpForm.qty;
    try {
      if (order && order.token) {
        normalizedQty = await normalizeQuantity(order.token, tpForm.qty);
        
        // Check if normalized quantity is different from entered quantity
        if (parseFloat(normalizedQty) !== parseFloat(tpForm.qty)) {
          setQtyValidationMessage(`System suggestion : ${tpForm.qty} is not valid. Minimum quantity step rounded to: ${normalizedQty}`);
          setTpForm(prev => ({ ...prev, qty: normalizedQty.toString() }));
          return;
        }
      }
    } catch (err) {
      toast.error('Error normalizing quantity: ' + err.message);
      return;
    }
    
    setTpLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/orders/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: order.token,
          count: normalizedQty,
          orderType: 'Market',
          side: order.side === 'Buy' ? 'Sell' : 'Buy',
          algorithm: order.Algorithm,
          price: tpForm.price,
          LinkedOrderId: order._id,
          orderNature: 'tp',
          positionIdx: order.positionIdx,
          takeProfit: tpForm.price,
        }),
        credentials: 'include',
      });
      const data = await res.json();
      let placedOrderId = null;
      if (data.order && data.order.orderId) {
        placedOrderId = data.order.orderId;
      } else if (data.orderId) {
        placedOrderId = data.orderId;
      }
      if (data.bybitRes && data.bybitRes.message) {
        if (data.bybitRes.message === 'OK') {
          toast.success('Take Profit order placed successfully!', { position: 'top-right', autoClose: 5000 });
          // Show order success modal with all details
          openOrderSuccess({
            orderId: data._id,
            symbol: order.token,
            side: order.side === 'Buy' ? 'Sell' : 'Buy', // TP is opposite of original order
            positionIdx: order.positionIdx,
            isSuccess: true
          });
        } else if (data.bybitRes.message.includes('trigger')) {
          toast.error('Error code 101 -  please validate price input value and use close button option', { position: 'top-right', autoClose: 5000 });
          // Show order failure modal
          openOrderSuccess({
            symbol: order.token,
            side: order.side === 'Buy' ? 'Sell' : 'Buy',
            positionIdx: order.positionIdx,
            isSuccess: false,
            errorMessage: 'Error code 101 - please validate price input value and use close button option'
          });
        } else {
          const cleanedMessage = cleanBybitFromMessage(data.bybitRes.message) || 'Take Profit order failed!';
          toast.error(cleanedMessage, { position: 'top-right', autoClose: 5000 });
          // Show order failure modal
          openOrderSuccess({
            symbol: order.token,
            side: order.side === 'Buy' ? 'Sell' : 'Buy',
            positionIdx: order.positionIdx,
            isSuccess: false,
            errorMessage: cleanedMessage
          });
        }
      } else if (data.success) {
        toast.success('Take Profit order placed successfully!', { position: 'top-right', autoClose: 5000 });
        // Show order success modal with all details
        openOrderSuccess({
          orderId: data._id,
          symbol: order.token,
          side: order.side === 'Buy' ? 'Sell' : 'Buy',
          positionIdx: order.positionIdx,
          isSuccess: true
        });
      } else {
        const cleanedMessage = cleanBybitFromMessage(data.error) || 'Take Profit order failed!';
        toast.error(cleanedMessage, { position: 'top-right', autoClose: 5000 });
        // Show order failure modal
        openOrderSuccess({
          symbol: order.token,
          side: order.side === 'Buy' ? 'Sell' : 'Buy',
          positionIdx: order.positionIdx,
          isSuccess: false,
          errorMessage: cleanedMessage
        });
      }

      // Immediately fetch the latest status for the new TP order
      if (placedOrderId) {
        try {
          await fetch(`${BASE_URL}/api/orders/status/${placedOrderId}`, { credentials: 'include' });
        } catch (e) {
          // ignore error, just try to update status
        }
      }
      fetchOrders();
      fetchTpOrders(order._id);
      setOrdersTableRefreshKey(prev => prev + 1);
      setQtyValidationMessage(''); // Clear validation message on success
      setTpForm({ qty: '', price: '' }); // Clear form on success
    } catch (err) {
      console.error(err);
      toast.error('Failed to place Take Profit order', { position: 'top-right', autoClose: 5000 });
      // Show order failure modal for catch errors
      openOrderSuccess({
        symbol: order.token,
        side: order.side === 'Buy' ? 'Sell' : 'Buy',
        positionIdx: order.positionIdx,
        isSuccess: false,
        errorMessage: 'Failed to place Take Profit order'
      });
    } finally {
      setTpLoading(false);
    }
  };

  // Handle close all orders
  const handleCloseAllOrders = async () => {
    if (!order || !order._id) return;
    try {
      const res = await fetch(`${BASE_URL}/api/orders/cancel-all/${order._id}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        toast.success('All related orders cancelled!', { position: 'top-right', autoClose: 5000 });
        fetchOrders();
        setOrdersTableRefreshKey(prev => prev + 1);
      } else {
        toast.error(data.error || 'Failed to cancel all orders', { position: 'top-right', autoClose: 5000 });
      }
    } catch (err) {
      toast.error('Failed to cancel all orders', { position: 'top-right', autoClose: 5000 });
    }
  };
  // Refresh TP orders whenever refreshKey changes (after cancel or submit)
  useEffect(() => {
    if (order && order._id) {
      fetchTpOrders(order._id);
    }
  }, [ordersTableRefreshKey, order]);

  if (!visible || !order) return null;

  const infoRows = [
    { label: 'Filled Quantity', value: order.filledQuantity || order.count || '-' },
    { label: 'Entry Price', value: order.entryPrice || '-' },
    { label: 'Current Price', value: liveCurrentPrice || '-' },
    { label: 'Quantity on hold', value: onHold },
    { label: 'Sold quantity', value: sold },
  ];

  // Copy available quantity to clipboard
  const handleCopyAvailable = () => {
    if (maxQty !== undefined && maxQty !== null) {
      navigator.clipboard.writeText(maxQty.toString());
      toast.success('Copied!', { position: 'top-right', autoClose: 1500 });
    }
  };
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.25)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          width: '99%',
          maxWidth: 800,
          minWidth: 400,
          padding: 32,
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          fontFamily: 'Roboto, Segoe UI, Arial, sans-serif',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 10, color: '#222', textAlign: 'center', letterSpacing: 0.5 }}>Take Profit</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {infoRows.map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: 15 }}>
              <span style={{ color: '#868484ff', fontWeight: 350 }}>{row.label}</span>
              <span style={{ color: '#222', fontWeight: 600 }}>{row.value}</span>
            </div>
          ))}
          {/* Available Quantity row with copy icon */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: 15 }}>
            <span style={{ color: '#868484ff', fontWeight: 350 }}>Available Quantity</span>
            <span style={{ color: '#222', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              {maxQty}
              <span
                onClick={handleCopyAvailable}
                style={{ cursor: 'pointer', marginLeft: 4, color: '#555', fontSize: 17, display: 'inline-flex', alignItems: 'center' }}
                title="Copy"
              >
                <CopyOutlined />
              </span>
            </span>
          </div>
        </div>
        {/* Close All Orders Button */}
        {/* <button
          onClick={handleCloseAllOrders}
          style={{
            background: 'red',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            padding: '10px 0',
            fontWeight: 600,
            fontSize: 16,
            margin: '12px 0 18px 0',
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Close
        </button> */}
         {/* <button
    onClick={handleCloseAllOrders}
    style={{
      background: 'red',
      color: 'white',
      border: 'none',
      borderRadius: 4,
      padding: '7px 0',
      fontWeight: 600,
      fontSize: 14,
      width: 70,
      minWidth: 50,
      cursor: 'pointer',
      marginRight: 0
    }}
  >
    Close
  </button> */}
        <div style={{ fontWeight: 600, fontSize: 16, margin: '18px 0 8px 0', color: '#222' }}>Take profit order</div>
        {showCloseOption && (
          <div style={{ 
            background: '#fff7e6', 
            border: '1px solid #ffd591', 
            borderRadius: 4, 
            padding: '8px 12px', 
            marginBottom: 10, 
            color: '#d48806', 
            fontSize: 14, 
            fontWeight: 500 
          }}>
            Take profit is near current price - consider using CLOSE option
          </div>
        )}
        {qtyValidationMessage && (
          <div style={{ 
            background: '#fff2f0', 
            border: '1px solid #ffccc7', 
            borderRadius: 4, 
            padding: '8px 12px', 
            marginBottom: 10, 
            color: '#cf1322', 
            fontSize: 14, 
            fontWeight: 500 
          }}>
            {qtyValidationMessage}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
          <input
            name="qty"
            type="number"
            placeholder="Quantity"
            value={tpForm.qty}
            onChange={handleTpInput}
            min={0}
            max={maxQty}
            style={{ flex: 1, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 15 }}
            autoComplete="off"
            autoFill="off"
          />
          <input
            name="price"
            type="number"
            placeholder="Price"
            value={tpForm.price}
            onChange={handleTpInput}
            style={{ flex: 1, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 15 }}
            autoComplete="off"
            autoFill="off"
          />
        </div>
        <button
          onClick={handleTpSubmit}
          disabled={tpLoading || !tpForm.qty || !tpForm.price}
          style={{
            background: 'black',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            padding: '10px 0',
            fontWeight: 600,
            fontSize: 16,
            cursor: tpLoading ? 'not-allowed' : 'pointer',
            opacity: tpLoading ? 0.7 : 1,
            transition: 'opacity 0.2s',
            marginBottom: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }}
        >
          {tpLoading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <SpinnerSmall size={16} color="#fff" />
              <span style={{ fontWeight: 700 }}>Placing...</span>
            </span>
          ) : 'Submit'}
        </button>
        {order && order._id && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 500, fontSize: 15, color: '#222', marginBottom: 4 }}>Already placed TP orders:</div>
            <OrdersTable 
              orderID={order._id} 
              orderType="tp" 
              refreshKey={ordersTableRefreshKey}
              onOrderUpdate={() => {
                // When an order is cancelled, update quantities immediately
                fetchOrders();
                fetchTpOrders(order._id);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
   
};

export default TakeProfitPopup;